import React, { useEffect, useMemo, useState } from "react";
import { API } from "@/lib/api";
import resolveImageUrl from "@/lib/imageUrl";

// Reusable image-picker modal.
// Props:
//   authed  – axios instance with credentials
//   onPick  – (asset: {url, alt_text, filename, id}) => void
//   onClose – () => void
export default function ImagePicker({ authed, onPick, onClose }) {
  const [tab, setTab] = useState("library");
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const load = async () => {
    setItems(null);
    const r = await authed.get(`/admin/uploads/list?limit=300${q ? `&q=${encodeURIComponent(q)}` : ""}`);
    setItems(r.data.items || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { const id = setTimeout(load, 250); return () => clearTimeout(id); /* eslint-disable-next-line */ }, [q]);

  const upload = async (file) => {
    setError(""); setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await authed.post("/admin/uploads/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const asset = { id: r.data.id, url: r.data.url || r.data.path, filename: r.data.name, alt_text: "" };
      onPick(asset);
    } catch (ex) {
      setError(ex.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-ink/60 flex items-start md:items-center justify-center overflow-y-auto p-4"
         data-testid="image-picker" onClick={onClose}>
      <div className="bg-white w-full max-w-5xl rounded-xl shadow-2xl my-6" onClick={e => e.stopPropagation()}>
        <div className="p-5 border-b border-line flex items-center justify-between gap-4">
          <div>
            <div className="label-mono text-terracotta text-[10px]">SELECT IMAGE</div>
            <h3 className="font-editorial text-2xl tracking-tighter mt-0.5">Media picker</h3>
          </div>
          <button onClick={onClose} className="text-ink/50 hover:text-ink text-2xl leading-none px-2">×</button>
        </div>
        <div className="px-5 pt-4 flex gap-2 border-b border-line">
          {[["library", "Choose from library"], ["upload", "Upload new"]].map(([k, l]) => (
            <button key={k} data-testid={`picker-tab-${k}`} onClick={() => setTab(k)}
                    className={`text-xs uppercase tracking-widest px-3 py-2 ${tab === k ? "border-b-2 border-terracotta text-terracotta -mb-px" : "text-ink/60 hover:text-ink"}`}>{l}</button>
          ))}
        </div>

        {tab === "library" && (
          <div className="p-5" data-testid="picker-library">
            <div className="mb-4 flex items-center gap-3">
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search filename or alt…"
                     data-testid="picker-search"
                     className="w-full border border-line px-3 py-2 text-sm" />
              <div className="text-[11px] text-ink/50 whitespace-nowrap">{items ? `${items.length} photo${items.length === 1 ? "" : "s"}` : "…"}</div>
            </div>
            {items === null ? (
              <div className="text-ink/40 text-sm py-12 text-center">Loading library…</div>
            ) : items.length === 0 ? (
              <div className="text-ink/40 text-sm py-12 text-center">No images yet. Switch to "Upload new" to add one.</div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[60vh] overflow-y-auto">
                {items.map(it => (
                  <button key={it.id} onClick={() => onPick(it)} data-testid={`picker-item-${it.id}`}
                          className="group text-left border border-line hover:border-terracotta transition-colors overflow-hidden">
                    <div className="aspect-square bg-line/20 overflow-hidden relative">
                      <img src={resolveImageUrl(it.url)} alt={it.alt_text || it.filename}
                           className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
                           onError={e => { e.currentTarget.style.display = "none"; e.currentTarget.nextSibling.style.display = "flex"; }} />
                      <div className="absolute inset-0 hidden items-center justify-center flex-col text-ink/40 text-[10px] uppercase tracking-widest bg-line/10">
                        <div className="text-2xl mb-1">⚠</div>
                        <div>file missing</div>
                      </div>
                    </div>
                    <div className="p-2">
                      <div className="text-[11px] truncate font-mono text-ink/70">{it.filename}</div>
                      {it.alt_text && <div className="text-[10px] text-ink/50 truncate italic">{it.alt_text}</div>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "upload" && (
          <div className="p-5" data-testid="picker-upload">
            <label className="block border-2 border-dashed border-line rounded-lg p-10 text-center hover:border-terracotta cursor-pointer transition-colors">
              <input type="file" accept="image/*" className="hidden"
                     onChange={e => e.target.files?.[0] && upload(e.target.files[0])}
                     disabled={uploading}
                     data-testid="picker-upload-input" />
              <div className="text-4xl text-ink/30">↑</div>
              <div className="mt-3 label-mono text-terracotta">CLICK TO UPLOAD</div>
              <div className="mt-1 text-xs text-ink/60">PNG, JPG, WEBP or SVG · up to 8 MB</div>
              {uploading && <div className="mt-3 text-xs text-ink/60">Uploading…</div>}
              {error && <div className="mt-3 text-xs text-red-600">{error}</div>}
            </label>
            <div className="mt-4 text-[11px] text-ink/50">Uploaded images are added to the shared Media Library and can be reused across every section without re-uploading.</div>
          </div>
        )}
      </div>
    </div>
  );
}
